import React from 'react';
import { Box, Typography, createTheme, ThemeProvider, CssBaseline } from '@mui/material';
import { useHistory } from 'react-router-dom';
import cryptoLogo from '../../assets/logo/logo.png';       // brand lockup (mark + wordmark)
import binanceLogo from '../../logos/binance.svg';          // crisp SVG (assets/logo/binance.png is a broken .ico)
import alpacaLogo from '../../assets/logo/alpaca.png';

/*
 * Cryptologics-AI marketing landing page.
 * Rebuilt to match the design handoff (README.md + "Cryptologics AI Landing.dc.html"):
 * dark institutional-fintech, cyan→blue accent, IBM Plex Mono numerals, multi-asset
 * (stocks + crypto) across Binance / Binance.US / Alpaca. Single primary CTA → sign up.
 */

// ─── Design tokens (from the handoff) ──────────────────────────────────────
const C = {
  bg: '#0a0c11',
  bgFooter: '#070a0f',
  text: '#f4f7fc',
  bright: '#f2f6ff',
  body: '#9aa6bd',
  muted: '#7b88a2',
  faint: '#5e6a82',
  accA: '#4d8fc7',      // steel blue — primary accent (matches Portfolio)
  accB: '#3f6fb0',      // gradient pair
  positive: '#46b88a',
  negative: '#d97b72',
  binance: '#f0b90b',
  alpaca: '#fcd535',
  cardBg: '#0f1218',
  cardBorder: 'rgba(255,255,255,.06)',
  hairline: 'rgba(255,255,255,.06)',
};
const MONO = '"IBM Plex Mono", ui-monospace, "SF Mono", Menlo, monospace';
const SANS = '"IBM Plex Sans", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';

const darkTheme = createTheme({
  palette: { mode: 'dark', background: { default: C.bg, paper: '#101a2e' } },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: { backgroundColor: `${C.bg} !important` },
        html: { backgroundColor: `${C.bg} !important` },
        '#root': { backgroundColor: `${C.bg} !important` },
        '@keyframes cl-pulse': { '0%,100%': { opacity: 0.45 }, '50%': { opacity: 1 } },
      },
    },
  },
});

// ─── Small building blocks ─────────────────────────────────────────────────
// Brand lockup image (mark + "CRYPTOLOGICS-AI" wordmark, transparent bg)
const CryptoLogo = ({ height = 30 }) => (
  <Box component="img" src={cryptoLogo} alt="Cryptologics-AI" sx={{ height, width: 'auto', display: 'block' }} />
);

// Exchange logo image (used in the Supported Exchanges band)
const ExchangeMark = ({ src, alt }) => (
  <Box component="img" src={src} alt={alt} sx={{ width: 24, height: 24, display: 'block', objectFit: 'contain' }} />
);

const PulseDot = ({ size = 8, color = C.positive }) => (
  <Box component="span" sx={{
    width: size, height: size, borderRadius: '50%', bgcolor: color,
    boxShadow: `0 0 10px ${color}b3`, display: 'inline-block',
    animation: 'cl-pulse 2s ease-in-out infinite',
  }} />
);

const DiscordIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="#a39bff">
    <path d="M20 4a18 18 0 0 0-4.5-1.4l-.3.6a14 14 0 0 0-6.4 0l-.3-.6A18 18 0 0 0 4 4 19 19 0 0 0 .5 17.5 18 18 0 0 0 6 20l.7-1.2a11 11 0 0 1-1.8-.9l.4-.3a12 12 0 0 0 10.4 0l.4.3c-.6.4-1.2.7-1.8.9L15 20a18 18 0 0 0 5.5-2.5A19 19 0 0 0 20 4ZM8.5 14.6c-.9 0-1.6-.8-1.6-1.8s.7-1.8 1.6-1.8 1.6.8 1.6 1.8-.7 1.8-1.6 1.8Zm7 0c-.9 0-1.6-.8-1.6-1.8s.7-1.8 1.6-1.8 1.6.8 1.6 1.8-.7 1.8-1.6 1.8Z" />
  </svg>
);

// 24px line icon wrapper for feature tiles
const Icon = ({ children }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={C.accA} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {children}
  </svg>
);

const FEATURES = [
  { title: 'Ultra-Low Latency', icon: <Icon><path d="M12 14a8 8 0 1 1 8-8" /><path d="M12 14l4-4" /></Icon>,
    desc: 'Lightning-fast trade execution with minimal latency to ensure your orders are placed at the best possible prices.' },
  { title: 'High Frequency Trading', icon: <Icon><polyline points="3 17 9 11 13 15 21 7" /><polyline points="15 7 21 7 21 13" /></Icon>,
    desc: 'Execute trades on tick-level data for maximum precision and speed in volatile market conditions.' },
  { title: 'Secure & Encrypted', icon: <Icon><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></Icon>,
    desc: 'Your data is encrypted and securely stored. API keys are protected with industry-standard encryption.' },
  { title: 'TradingView Alerts', icon: <Icon><path d="M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M10.5 21a1.5 1.5 0 0 0 3 0" /></Icon>,
    desc: 'Connect your TradingView alerts to automatically trigger bot trades based on your custom indicators.' },
  { title: 'Advanced Analytics', icon: <Icon><line x1="6" y1="20" x2="6" y2="12" /><line x1="12" y1="20" x2="12" y2="6" /><line x1="18" y1="20" x2="18" y2="14" /></Icon>,
    desc: 'Comprehensive backtesting with historical data to validate your strategies before going live.' },
  { title: 'Paper Trading', icon: <Icon><path d="M9 3h6" /><path d="M10 3v6l-5 9a2 2 0 0 0 2 3h10a2 2 0 0 0 2-3l-5-9V3" /><path d="M7 15h10" /></Icon>,
    desc: 'Test your strategies risk-free with simulated trading before going live with real capital.' },
];

const STATS = [
  { value: '24/7', label: 'MARKET MONITORING' },
  { value: '10ms', label: 'EXECUTION LATENCY' },
  { value: '99.9%', label: 'PLATFORM UPTIME' },
  { value: '200+', label: 'MARKETS COVERED', accent: true },
];

// ─── Hero product-dashboard mock ───────────────────────────────────────────
const EquityCurve = () => (
  <Box sx={{ px: '8px', pt: '12px', color: C.accA }}>
    <svg viewBox="0 0 540 168" preserveAspectRatio="none" style={{ width: '100%', height: 150, display: 'block' }}>
      <defs>
        <linearGradient id="clArea" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="currentColor" stopOpacity="0.26" />
          <stop offset="1" stopColor="currentColor" stopOpacity="0" />
        </linearGradient>
      </defs>
      <g stroke="rgba(255,255,255,.05)" strokeWidth="1">
        <line x1="0" y1="48" x2="540" y2="48" />
        <line x1="0" y1="96" x2="540" y2="96" />
        <line x1="0" y1="144" x2="540" y2="144" />
      </g>
      <path d="M0 140 C60 134 96 122 150 116 C214 109 244 86 300 88 C356 90 388 52 430 44 C470 36 500 30 540 22 L540 168 L0 168 Z" fill="url(#clArea)" />
      <path d="M0 140 C60 134 96 122 150 116 C214 109 244 86 300 88 C356 90 388 52 430 44 C470 36 500 30 540 22" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="540" cy="22" r="4" fill="currentColor" />
    </svg>
  </Box>
);

const PositionRow = ({ mark, markColor, markBg, markBorder, markWeight, symbol, price, change, topBorder }) => (
  <Box sx={{
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: '22px', py: '13px',
    borderTop: topBorder ? '1px solid rgba(255,255,255,.04)' : 'none',
  }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
      <Box component="span" sx={{
        width: 26, height: 26, borderRadius: '50%', bgcolor: markBg, border: `1px solid ${markBorder}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center', color: markColor,
        fontSize: mark === '₿' ? 13 : 11, fontWeight: markWeight,
      }}>{mark}</Box>
      <Typography component="span" sx={{ fontSize: '13.5px', fontWeight: 600, color: '#dbe3f2' }}>{symbol}</Typography>
    </Box>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
      <Typography component="span" sx={{ fontFamily: MONO, fontVariantNumeric: 'tabular-nums', fontSize: '13px', color: '#aeb9cf' }}>{price}</Typography>
      <Typography component="span" sx={{ fontFamily: MONO, fontVariantNumeric: 'tabular-nums', fontSize: '12.5px', fontWeight: 600, color: C.positive }}>{change}</Typography>
    </Box>
  </Box>
);

const HeroDashboard = () => (
  <Box sx={{ position: 'relative', width: '100%', maxWidth: 540, ml: { md: 'auto' }, mx: { xs: 'auto', md: 'unset' } }}>
    {/* cyan glow behind the card */}
    <Box sx={{
      position: 'absolute', inset: '-14% -8%', borderRadius: '50%',
      background: `radial-gradient(closest-side, rgba(77,143,199,.12), transparent 72%)`, pointerEvents: 'none',
    }} />
    <Box sx={{
      position: 'relative', background: 'linear-gradient(180deg,#101a2e,#0a1220)',
      border: '1px solid rgba(255,255,255,.08)', borderRadius: '18px',
      boxShadow: '0 34px 80px rgba(0,0,0,.5)', overflow: 'hidden',
    }}>
      {/* header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: '22px', py: '16px', borderBottom: '1px solid rgba(255,255,255,.06)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
          <PulseDot />
          <Typography component="span" sx={{ fontSize: '13px', color: '#aeb9cf', fontWeight: 600 }}>Portfolio · Live</Typography>
        </Box>
        <Typography component="span" sx={{ fontFamily: MONO, fontSize: '11px', letterSpacing: '.1em', color: '#6f7c96' }}>MULTI-ASSET · LIVE</Typography>
      </Box>
      {/* balance */}
      <Box sx={{ px: '22px', pt: '22px', pb: '8px' }}>
        <Typography sx={{ fontSize: '12.5px', color: '#8593ad', letterSpacing: '.02em' }}>Total balance</Typography>
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '12px', mt: '7px' }}>
          <Typography component="span" sx={{ fontFamily: MONO, fontVariantNumeric: 'tabular-nums', fontSize: '33px', fontWeight: 600, letterSpacing: '-.01em', color: C.bright }}>$128,540.22</Typography>
          <Typography component="span" sx={{
            fontFamily: MONO, fontVariantNumeric: 'tabular-nums', fontSize: '13px', fontWeight: 600, color: C.positive,
            bgcolor: 'rgba(70,184,138,.1)', border: '1px solid rgba(70,184,138,.26)', px: '9px', py: '3px', borderRadius: '7px', mb: '7px',
          }}>▲ 24.5%</Typography>
        </Box>
      </Box>
      <EquityCurve />
      {/* mini stats */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', borderTop: '1px solid rgba(255,255,255,.06)' }}>
        {[['68.4%', 'Win rate'], ['2.41', 'Sharpe'], ['1,284', 'Trades']].map(([v, l], i) => (
          <Box key={l} sx={{ px: '20px', py: '15px', borderRight: i < 2 ? '1px solid rgba(255,255,255,.06)' : 'none' }}>
            <Typography sx={{ fontFamily: MONO, fontVariantNumeric: 'tabular-nums', fontSize: '18px', fontWeight: 600, color: '#e8edf7' }}>{v}</Typography>
            <Typography sx={{ fontSize: '11.5px', color: C.muted, mt: '3px' }}>{l}</Typography>
          </Box>
        ))}
      </Box>
      {/* positions — one crypto + one stock to signal multi-asset */}
      <Box sx={{ borderTop: '1px solid rgba(255,255,255,.06)' }}>
        <PositionRow mark="₿" markColor="#f7931a" markBg="rgba(247,147,26,.12)" markBorder="rgba(247,147,26,.35)" markWeight={700} symbol="BTC/USDT" price="$67,420" change="+3.2%" />
        <PositionRow mark="N" markColor="#9bd64a" markBg="rgba(118,185,0,.12)" markBorder="rgba(118,185,0,.4)" markWeight={800} symbol="NVDA" price="$132.40" change="+1.8%" topBorder />
      </Box>
    </Box>
  </Box>
);

// ─── Page ──────────────────────────────────────────────────────────────────
function LandingPageContent() {
  const history = useHistory();
  const goSignUp = () => history.push('/sign-up');
  const goSignIn = () => history.push('/sign-in');
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const navLinkSx = { cursor: 'pointer', color: C.body, transition: 'color .2s ease', '&:hover': { color: '#e8edf7' } };

  const primaryBtn = {
    fontFamily: SANS, fontWeight: 600, fontSize: '15px', color: '#06121f', bgcolor: C.accA,
    px: '32px', py: '15px', borderRadius: '9px', boxShadow: '0 8px 22px rgba(77,143,199,.22)',
    cursor: 'pointer', display: 'inline-block', border: 'none',
    transition: 'transform .2s ease, box-shadow .2s ease',
    '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 12px 30px rgba(77,143,199,.36)', bgcolor: C.accA },
  };

  return (
    <Box sx={{ position: 'relative', minHeight: '100vh', bgcolor: C.bg, color: '#e8edf7', fontFamily: SANS, overflowX: 'hidden', '& *': { boxSizing: 'border-box' } }}>
      {/* ambient bg glow + tech grid */}
      <Box sx={{ position: 'absolute', top: -200, left: '50%', transform: 'translateX(-50%)', width: 1100, height: 700, background: 'radial-gradient(closest-side, rgba(77,143,199,.07), transparent 70%)', pointerEvents: 'none' }} />
      <Box sx={{
        position: 'absolute', top: 0, left: 0, right: 0, height: 1100, pointerEvents: 'none',
        backgroundImage: 'linear-gradient(rgba(255,255,255,.022) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.022) 1px,transparent 1px)',
        backgroundSize: '56px 56px',
        maskImage: 'radial-gradient(circle at 50% 14%, #000, transparent 62%)',
        WebkitMaskImage: 'radial-gradient(circle at 50% 14%, #000, transparent 62%)',
      }} />

      {/* NAV */}
      <Box sx={{
        position: 'sticky', top: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        px: { xs: '24px', md: '56px' }, py: '18px',
        background: 'rgba(11,16,32,.78)', backdropFilter: 'blur(14px)', borderBottom: `1px solid ${C.hairline}`,
      }}>
        <CryptoLogo height={40} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: '18px', md: '36px' } }}>
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: '30px', fontWeight: 500, fontSize: '14px' }}>
            <Typography component="span" sx={navLinkSx} onClick={() => scrollTo('capabilities')}>Platform</Typography>
            <Typography component="span" sx={navLinkSx} onClick={() => scrollTo('capabilities')}>Capabilities</Typography>
            <Typography component="span" sx={navLinkSx} onClick={() => scrollTo('stats')}>Security</Typography>
          </Box>
          <Box component="button" onClick={goSignIn} sx={{
            fontFamily: SANS, fontWeight: 600, fontSize: '13.5px', color: '#e8edf7', bgcolor: 'transparent',
            px: '22px', py: '10px', border: '1px solid rgba(255,255,255,.16)', borderRadius: '8px', cursor: 'pointer',
            transition: 'border-color .2s ease, background .2s ease',
            '&:hover': { borderColor: 'rgba(77,143,199,.5)', background: 'rgba(77,143,199,.08)' },
          }}>Sign in</Box>
        </Box>
      </Box>

      {/* HERO */}
      <Box sx={{
        position: 'relative', zIndex: 1, maxWidth: 1240, mx: 'auto', px: { xs: '24px', md: '56px' }, pt: { xs: '56px', md: '88px' }, pb: { xs: '64px', md: '96px' },
        display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.02fr .98fr' }, gap: { xs: '48px', md: '56px' }, alignItems: 'center',
      }}>
        <Box>
          <Box sx={{ fontFamily: MONO, fontWeight: 500, fontSize: '12px', letterSpacing: '.22em', color: C.accA, mb: '22px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <PulseDot size={7} />AUTOMATED TRADING · STOCKS &amp; CRYPTO
          </Box>
          <Typography component="h1" sx={{ fontFamily: SANS, fontWeight: 800, fontSize: 'clamp(36px, 6vw, 62px)', lineHeight: 1.04, letterSpacing: '-.025em', m: '0 0 26px', color: C.text }}>
            Institutional-grade trading automation
          </Typography>
          <Typography sx={{ fontSize: '18px', lineHeight: 1.7, color: C.body, maxWidth: 520, m: '0 0 38px' }}>
            Execute sophisticated strategies 24/7 on an ultra-low-latency engine built for professionals. Institutional infrastructure, an intuitive interface.
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px', mb: '34px', flexWrap: 'wrap' }}>
            <Box component="button" onClick={goSignUp} sx={primaryBtn}>Start trading free</Box>
            <Box component="button" onClick={() => scrollTo('capabilities')} sx={{
              fontFamily: SANS, fontWeight: 600, fontSize: '15px', color: '#cdd6e8', bgcolor: 'transparent',
              px: '26px', py: '15px', borderRadius: '9px', border: '1px solid rgba(255,255,255,.14)', cursor: 'pointer',
              transition: 'border-color .2s ease, color .2s ease',
              '&:hover': { borderColor: 'rgba(77,143,199,.5)', color: '#fff' },
            }}>View platform</Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '22px', fontFamily: MONO, fontSize: '12.5px', color: C.muted }}>
            <span>No card required</span>
            <Box component="span" sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: '#3a475e' }} />
            <span>99.9% uptime</span>
          </Box>
        </Box>
        <HeroDashboard />
      </Box>

      {/* SUPPORTED EXCHANGES */}
      <Box id="exchanges" sx={{
        position: 'relative', zIndex: 1, borderTop: `1px solid ${C.hairline}`, borderBottom: `1px solid ${C.hairline}`,
        background: 'linear-gradient(180deg, rgba(255,255,255,.015), transparent)', px: { xs: '24px', md: '56px' }, py: '46px', textAlign: 'center',
      }}>
        <Typography sx={{ fontFamily: MONO, fontWeight: 500, fontSize: '12px', letterSpacing: '.24em', color: C.muted, mb: '26px' }}>SUPPORTED EXCHANGES</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px', flexWrap: 'wrap' }}>
          {[
            { mark: <ExchangeMark src={binanceLogo} alt="Binance" />, name: <Box component="span" sx={{ color: C.binance }}>Binance</Box> },
            { mark: <ExchangeMark src={binanceLogo} alt="Binance.US" />, name: <Box component="span" sx={{ color: C.binance }}>Binance<Box component="span" sx={{ color: '#c9d2e3' }}>.US</Box></Box> },
            { mark: <ExchangeMark src={alpacaLogo} alt="Alpaca" />, name: <Box component="span" sx={{ color: '#ffd60a' }}>Alpaca</Box> },
          ].map((ex, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: '13px', px: '24px', py: '14px', border: '1px solid rgba(255,255,255,.09)', bgcolor: 'rgba(255,255,255,.03)', borderRadius: '12px' }}>
              {ex.mark}
              <Typography component="span" sx={{ fontFamily: SANS, fontWeight: 700, fontSize: '19px' }}>{ex.name}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* FEATURES */}
      <Box id="capabilities" sx={{ position: 'relative', zIndex: 1, maxWidth: 1240, mx: 'auto', px: { xs: '24px', md: '56px' }, pt: { xs: '72px', md: '100px' }, pb: '70px' }}>
        <Box sx={{ textAlign: 'center', mb: '60px' }}>
          <Typography sx={{ fontFamily: MONO, fontWeight: 500, fontSize: '12px', letterSpacing: '.22em', color: C.accA, mb: '18px' }}>CAPABILITIES</Typography>
          <Typography component="h2" sx={{ fontFamily: SANS, fontWeight: 700, fontSize: 'clamp(30px, 4.5vw, 42px)', letterSpacing: '-.02em', m: '0 0 16px', color: C.text }}>Professional trading tools</Typography>
          <Typography sx={{ fontSize: '18px', lineHeight: 1.6, color: C.body, maxWidth: 600, mx: 'auto' }}>Everything you need to trade like an institution, without the complexity.</Typography>
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2,1fr)', md: 'repeat(3,1fr)' }, gap: '20px' }}>
          {FEATURES.map((f) => (
            <Box key={f.title} sx={{
              background: C.cardBg, border: `1px solid ${C.cardBorder}`, borderRadius: '14px', p: '32px',
              transition: 'transform .25s ease, border-color .25s ease, box-shadow .25s ease',
              '&:hover': { transform: 'translateY(-3px)', borderColor: 'rgba(77,143,199,.4)', boxShadow: '0 16px 38px rgba(0,0,0,.34)' },
            }}>
              <Box sx={{ width: 46, height: 46, borderRadius: '11px', bgcolor: 'rgba(77,143,199,.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: '24px' }}>{f.icon}</Box>
              <Typography component="h3" sx={{ fontFamily: SANS, fontWeight: 700, fontSize: '21px', m: '0 0 12px', color: C.text }}>{f.title}</Typography>
              <Typography sx={{ fontSize: '15px', lineHeight: 1.65, color: C.body, m: 0 }}>{f.desc}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* STATS BAND */}
      <Box id="stats" sx={{ position: 'relative', zIndex: 1, borderTop: `1px solid ${C.hairline}`, background: 'linear-gradient(180deg, rgba(77,143,199,.04), transparent)', px: { xs: '24px', md: '56px' }, py: '60px' }}>
        <Box sx={{ maxWidth: 1100, mx: 'auto', display: 'grid', gridTemplateColumns: { xs: 'repeat(2,1fr)', md: 'repeat(4,1fr)' }, rowGap: '40px' }}>
          {STATS.map((s, i) => (
            <Box key={s.label} sx={{ textAlign: 'center', px: '24px', borderLeft: { xs: 'none', md: i > 0 ? '1px solid rgba(255,255,255,.07)' : 'none' } }}>
              <Typography sx={{ fontFamily: MONO, fontVariantNumeric: 'tabular-nums', fontWeight: 600, fontSize: '40px', lineHeight: 1, letterSpacing: '-.01em', color: s.accent ? C.accA : C.text }}>{s.value}</Typography>
              <Typography sx={{ fontFamily: MONO, fontSize: '12px', letterSpacing: '.12em', color: C.muted, mt: '14px' }}>{s.label}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* CTA */}
      <Box id="cta" sx={{ position: 'relative', zIndex: 1, maxWidth: 1240, mx: 'auto', px: { xs: '24px', md: '56px' }, py: { xs: '64px', md: '90px' } }}>
        <Box sx={{
          border: '1px solid rgba(77,143,199,.22)', borderRadius: '26px',
          background: 'radial-gradient(120% 140% at 50% 0%, rgba(77,143,199,.12), rgba(63,111,176,.04) 55%, transparent)',
          px: { xs: '28px', md: '56px' }, py: { xs: '56px', md: '80px' }, textAlign: 'center',
        }}>
          <Typography component="h2" sx={{ fontFamily: SANS, fontWeight: 800, fontSize: 'clamp(30px, 5vw, 46px)', letterSpacing: '-.025em', m: '0 0 18px', color: C.text }}>Ready to transform your trading?</Typography>
          <Typography sx={{ fontSize: '18px', lineHeight: 1.6, color: C.body, maxWidth: 560, mx: 'auto', mb: '36px' }}>Join traders using Cryptologics-AI to automate their strategies across stocks and crypto.</Typography>
          <Box component="button" onClick={goSignUp} sx={{ ...primaryBtn, px: '40px', py: '16px', boxShadow: '0 10px 28px rgba(77,143,199,.24)', '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 14px 36px rgba(77,143,199,.4)', bgcolor: C.accA } }}>Start trading free</Box>
          <Typography sx={{ fontFamily: MONO, fontSize: '12.5px', color: C.muted, mt: '20px', letterSpacing: '.04em' }}>No credit card required</Typography>
        </Box>
      </Box>

      {/* FOOTER */}
      <Box sx={{ position: 'relative', zIndex: 1, bgcolor: C.bgFooter, borderTop: `1px solid ${C.hairline}`, px: { xs: '24px', md: '56px' }, pt: '72px', pb: '56px' }}>
        <Box sx={{ maxWidth: 1240, mx: 'auto', display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.4fr 1fr' }, gap: '40px' }}>
          <Box>
            <Box sx={{ mb: '18px' }}><CryptoLogo height={38} /></Box>
            <Typography sx={{ fontSize: '15px', lineHeight: 1.7, color: '#8593ad', maxWidth: 420, m: '0 0 28px' }}>Institutional-grade trading automation for stocks and crypto, powered by artificial intelligence.</Typography>
            <Box component="a" href="https://discord.gg/b5x2HgFsS" target="_blank" rel="noopener noreferrer" sx={{
              display: 'inline-flex', alignItems: 'center', gap: '10px', fontFamily: SANS, fontWeight: 700, fontSize: '14px', color: '#e8edf7',
              border: '1px solid rgba(124,116,255,.35)', background: 'rgba(88,101,242,.12)', px: '22px', py: '12px', borderRadius: '11px',
              textDecoration: 'none', transition: 'all .2s ease',
              '&:hover': { background: 'rgba(88,101,242,.25)', borderColor: '#5865F2', transform: 'translateY(-1px)' },
            }}>
              <DiscordIcon size={20} />Join our Discord
            </Box>
          </Box>
          <Box>
            <Typography sx={{ fontFamily: SANS, fontWeight: 700, fontSize: '15px', mb: '20px', color: C.text }}>Quick Links</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px', fontSize: '15px' }}>
              {[
                { label: 'Features', id: 'capabilities' },
                { label: 'Supported Exchanges', id: 'exchanges' },
                { label: 'Stats', id: 'stats' },
                { label: 'Get Started', id: 'cta' },
              ].map((item) => (
                <Typography key={item.id} component="span" onClick={() => scrollTo(item.id)} sx={{ color: '#8593ad', cursor: 'pointer', transition: 'color .2s ease', '&:hover': { color: C.accA } }}>{item.label}</Typography>
              ))}
            </Box>
          </Box>
        </Box>
        <Box sx={{ maxWidth: 1240, mx: 'auto', mt: '48px', pt: '26px', borderTop: `1px solid ${C.hairline}`, fontSize: '13px', color: C.faint }}>
          © {new Date().getFullYear()} Cryptologics-AI. All rights reserved.
        </Box>
      </Box>
    </Box>
  );
}

export default function LandingPage() {
  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <LandingPageContent />
    </ThemeProvider>
  );
}
